import { defineStore, getActivePinia } from 'pinia';
import { emptySessionSelf } from '@/utils/sessionIdentity';
import { normalizeSelfProfile } from '@/utils/selfProfileForm';
import { meToSelfProfile } from '@/api2/selfProfileShape';
import { useSessionStore } from '@/stores/session';

/**
 * Optional explicit profile, else Pinia sessionSelf when active.
 * Utils (messagingSender, directivesSession, …) use this so callers need not
 * pass the store; pass `null` to say "no profile" in tests.
 */
export function peekSessionSelfProfile(explicit) {
  if (explicit !== undefined) return explicit;
  try {
    const pinia = getActivePinia();
    if (pinia) return useSessionSelfStore(pinia).profile;
  } catch (e) {
    /* Pinia not ready */
  }
  return null;
}

/**
 * The signed-in person in main's self-profile shape (#321's sessionSelf).
 *
 * On main this was keyon's MySQL `account_user` row, loaded by GetSelfProfile.
 * On the port it is an adapter over the token-backed session (decision D4,
 * MX-13): `loadSelf` takes `GET /me` from `useSessionStore()` — joining or
 * reusing its load, so this costs no request of its own — and maps it with the
 * bridge's one mapper (api2/selfProfileShape.js). No keyon call, and never
 * `allData`, which is the open chart. Clinical PHR stays in Vuex
 * `login/getAllData`.
 */
export const useSessionSelfStore = defineStore('sessionSelf', {
  state: () => ({
    profile: emptySessionSelf(),
    loading: false,
    error: null,
    /** `${accountnumber}:${unique_ID}` after a successful load */
    loadedKey: '',
    /** Join concurrent login/resume callers (same pattern as privileges). */
    inflight: null,
  }),

  getters: {
    isLoaded: (state) => !!state.loadedKey,
    unique_ID: (state) => String((state.profile && state.profile.unique_ID) || '').trim(),
    accountnumber: (state) =>
      String((state.profile && state.profile.accountnumber) || '').trim(),
  },

  actions: {
    clear() {
      this.profile = emptySessionSelf();
      this.error = null;
      this.loadedKey = '';
      this.loading = false;
      this.inflight = null;
    },

    /** Apply a known-good profile (e.g. after UpdateSelfProfile). */
    setFromProfile(raw) {
      const profile = normalizeSelfProfile(raw);
      this.profile = profile;
      this.error = null;
      const acct = String(profile.accountnumber || '').trim();
      const uid = String(profile.unique_ID || '').trim();
      this.loadedKey = acct && uid ? `${acct}:${uid}` : '';
    },

    /**
     * Load the signed-in person's profile.
     * Concurrent callers join the in-flight load.
     */
    async fetchForCurrentSession() {
      if (this.inflight) {
        await this.inflight;
        return;
      }
      const run = this.loadSelf();
      this.inflight = run;
      try {
        await run;
      } finally {
        if (this.inflight === run) this.inflight = null;
      }
    },

    async loadSelf() {
      this.loading = true;
      this.error = null;
      try {
        const session = useSessionStore();
        // `hydrate` joins a `GET /me` already in flight, and answers null
        // without a token; a loaded session is used as it is.
        const me = session.me || (await session.hydrate());
        if (!me) {
          // `hydrate` answers null only without a token: nobody is signed in,
          // so a profile still held here is a previous sign-in's and goes.
          // A failed request throws instead, and the catch below keeps the
          // profile so a transient failure does not blank the app.
          this.clear();
          this.error = session.error || 'missing_identity';
          return;
        }
        this.setFromProfile(meToSelfProfile(me));
      } catch (e) {
        this.error = (e && e.message) || 'Could not load self profile';
      } finally {
        this.loading = false;
      }
    },
  },
});
